import React, { useEffect, useState } from "react";
import { RefreshCw, ShieldCheck, Copy, Check } from "lucide-react";
import { api } from "../lib/api";

const inputStyle = {
  background: "var(--bg-surface)", border: "1px solid var(--border)",
  color: "var(--text-primary)", fontSize: 16,
};

// Distorted-text CAPTCHA. Fetches a fresh challenge whenever `resetKey` changes.
export function CaptchaField({ resetKey, answer, setAnswer, onId }) {
  const [img, setImg] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true); setImg(null);
    api.get("/captcha/new").then((r) => { setImg(r.data.image); onId(r.data.captcha_id); })
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [resetKey]);
  return (
    <div data-testid="captcha-field">
      <div className="text-[11px] uppercase tracking-widest mb-1.5" style={{ color: "var(--text-muted)" }}>Security check</div>
      <div className="flex items-center gap-2 mb-2">
        <div className="rounded-xl overflow-hidden grid place-items-center" style={{ background: "#fff", height: 66, width: 190, border: "1px solid var(--border)" }}>
          {img ? <img src={img} alt="captcha" data-testid="captcha-image" className="h-full w-full object-contain" />
               : <span style={{ color: "#999", fontSize: 12 }}>{loading ? "…" : ""}</span>}
        </div>
        <button type="button" onClick={load} data-testid="captcha-refresh" title="New image"
          className="h-11 w-11 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--overlay)", color: "var(--text-secondary)" }}>
          <RefreshCw size={16} />
        </button>
      </div>
      <input data-testid="captcha-input" value={answer} onChange={(e) => setAnswer(e.target.value.toUpperCase())}
        placeholder="Type the characters" autoComplete="off" autoCapitalize="characters" spellCheck={false}
        className="w-full rounded-xl px-4 py-3.5 text-sm outline-none tracking-[0.3em]" style={inputStyle} />
    </div>
  );
}

// Inline captcha "step 2" — replaces the sign-in / sign-up fields in place.
// Parent owns the captcha id/answer/key state and the verify action.
export function CaptchaStep({ onVerify, onBack, resetKey, answer, setAnswer, onId, error, loading, submitLabel = "Verify & continue" }) {
  return (
    <form onSubmit={(e) => { e.preventDefault(); onVerify(); }} data-testid="captcha-step" className="space-y-4">
      <div className="flex items-center gap-2 gold-text"><ShieldCheck size={18} /><span className="font-semibold text-sm">Security check</span></div>
      <p className="text-sm" style={{ color: "var(--text-secondary)" }}>One last step — confirm you're human to continue.</p>
      <CaptchaField resetKey={resetKey} answer={answer} setAnswer={setAnswer} onId={onId} />
      {error && <p className="text-xs" style={{ color: "#e06b70" }} data-testid="captcha-step-error">{error}</p>}
      <button type="submit" disabled={loading} data-testid="captcha-step-submit"
        className="w-full rounded-xl py-3.5 font-semibold text-sm" style={{ background: "var(--accent)", color: "#fff", opacity: loading ? 0.6 : 1 }}>
        {loading ? "Verifying…" : submitLabel}
      </button>
      <button type="button" onClick={onBack} data-testid="captcha-step-back"
        className="w-full text-center text-xs" style={{ color: "var(--text-secondary)" }}>Back</button>
    </form>
  );
}

// 6-digit authenticator code (with a "use a backup code" fallback).
export function MfaCodeForm({ onSubmit, error, loading, testid = "mfa-code-form" }) {
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState(false);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(code.trim()); }} data-testid={testid} className="space-y-3">
      <div className="flex items-center gap-2 gold-text"><ShieldCheck size={18} /><span className="font-semibold text-sm">Two-factor verification</span></div>
      <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
        {backup ? "Enter one of your saved backup codes." : "Enter the 6-digit code from your authenticator app."}
      </p>
      <input data-testid="mfa-code-input" value={code} onChange={(e) => setCode(e.target.value)} autoFocus
        inputMode={backup ? "text" : "numeric"} placeholder={backup ? "xxxx-xxxx" : "123456"}
        className="w-full rounded-xl px-4 py-3.5 text-sm outline-none tracking-[0.4em] text-center" style={inputStyle} />
      {error && <p className="text-xs" data-testid="mfa-error" style={{ color: "#e06b70" }}>{error}</p>}
      <button type="submit" disabled={loading} data-testid="mfa-submit"
        className="w-full rounded-xl py-3.5 font-semibold text-sm" style={{ background: "var(--accent)", color: "#fff", opacity: loading ? 0.6 : 1 }}>
        {loading ? "Verifying…" : "Verify"}
      </button>
      <button type="button" onClick={() => setBackup((b) => !b)} data-testid="mfa-toggle-backup"
        className="w-full text-center text-xs" style={{ color: "var(--text-secondary)" }}>
        {backup ? "Use authenticator app instead" : "Use a backup code"}
      </button>
    </form>
  );
}

// QR + secret enrollment step.
export function MfaSetupForm({ setup, onSubmit, error, loading, forced }) {
  const [code, setCode] = useState("");
  const [showKey, setShowKey] = useState(false);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(code.trim()); }} data-testid="mfa-setup-form" className="space-y-3">
      <div className="flex items-center gap-2 gold-text"><ShieldCheck size={18} /><span className="font-semibold text-sm">Set up two-factor authentication</span></div>
      <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
        {forced ? "For security, this account requires an authenticator app. " : ""}
        Scan this QR code with Google Authenticator, Authy, or 1Password, then enter the 6-digit code it shows.
      </p>
      {setup?.qr && (
        <div className="grid place-items-center">
          <img src={setup.qr} alt="Authenticator QR" data-testid="mfa-qr" className="rounded-xl" style={{ width: 190, height: 190, background: "#fff", padding: 8 }} />
        </div>
      )}
      <button type="button" onClick={() => setShowKey((s) => !s)} className="w-full text-center text-xs" style={{ color: "var(--text-secondary)" }}>
        {showKey ? "Hide setup key" : "Can't scan? Show setup key"}
      </button>
      {showKey && setup?.secret && (
        <div data-testid="mfa-secret" className="text-center text-sm rounded-lg py-2 tracking-widest break-all" style={{ background: "var(--overlay)", color: "var(--text-primary)" }}>{setup.secret}</div>
      )}
      <input data-testid="mfa-setup-code" value={code} onChange={(e) => setCode(e.target.value)}
        inputMode="numeric" placeholder="123456"
        className="w-full rounded-xl px-4 py-3.5 text-sm outline-none tracking-[0.4em] text-center" style={inputStyle} />
      {error && <p className="text-xs" data-testid="mfa-setup-error" style={{ color: "#e06b70" }}>{error}</p>}
      <button type="submit" disabled={loading} data-testid="mfa-setup-submit"
        className="w-full rounded-xl py-3.5 font-semibold text-sm" style={{ background: "var(--accent)", color: "#fff", opacity: loading ? 0.6 : 1 }}>
        {loading ? "Enabling…" : "Enable two-factor"}
      </button>
    </form>
  );
}

// One-time backup codes display shown once after enrollment.
export function BackupCodes({ codes, onDone }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(codes.join("\n")); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div data-testid="backup-codes" className="space-y-3">
      <div className="flex items-center gap-2 gold-text"><ShieldCheck size={18} /><span className="font-semibold text-sm">Save your backup codes</span></div>
      <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
        Store these somewhere safe. Each code works once if you lose access to your authenticator app.
      </p>
      <div className="grid grid-cols-2 gap-2 rounded-xl p-3" style={{ background: "var(--overlay)" }}>
        {codes.map((c) => <div key={c} className="text-center text-sm tracking-widest font-mono" style={{ color: "var(--text-primary)" }}>{c}</div>)}
      </div>
      <button type="button" onClick={copy} data-testid="backup-copy" className="w-full rounded-xl py-2.5 text-sm flex items-center justify-center gap-2" style={{ background: "var(--overlay)", color: "var(--text-secondary)" }}>
        {copied ? <><Check size={15} /> Copied</> : <><Copy size={15} /> Copy codes</>}
      </button>
      <button type="button" onClick={onDone} data-testid="backup-done" className="w-full rounded-xl py-3.5 font-semibold text-sm" style={{ background: "var(--accent)", color: "#fff" }}>
        I've saved them — continue
      </button>
    </div>
  );
}

export function passwordChecks(pw) {
  return {
    len: (pw || "").length >= 12,
    upper: /[A-Z]/.test(pw || ""),
    num: /[0-9]/.test(pw || ""),
    sym: /[^A-Za-z0-9]/.test(pw || ""),
  };
}
export function passwordValid(pw) { const c = passwordChecks(pw); return c.len && c.upper && c.num && c.sym; }

function Check_(ok, label) {
  return (
    <div className="flex items-center gap-1.5" style={{ color: ok ? "var(--accent)" : "var(--text-muted)" }}>
      <Check size={13} style={{ opacity: ok ? 1 : 0.35 }} /> <span className="text-[11px]">{label}</span>
    </div>
  );
}

// New password + confirm with a live strength checklist. Used for reset & change flows.
export function NewPasswordForm({ onSubmit, error, loading, title = "Set a new password", submitLabel = "Save password" }) {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const c = passwordChecks(pw);
  const match = pw.length > 0 && pw === confirm;
  const canSubmit = passwordValid(pw) && match && !loading;
  const inputStyle = { background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)", fontSize: 16 };
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (canSubmit) onSubmit(pw); }} data-testid="new-password-form" className="space-y-3">
      <div className="flex items-center gap-2 gold-text"><ShieldCheck size={18} /><span className="font-semibold text-sm">{title}</span></div>
      <input data-testid="new-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" autoComplete="new-password"
        className="w-full rounded-xl px-4 py-3.5 text-sm outline-none" style={inputStyle} />
      <input data-testid="confirm-password-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" autoComplete="new-password"
        className="w-full rounded-xl px-4 py-3.5 text-sm outline-none" style={inputStyle} />
      <div className="grid grid-cols-2 gap-1.5 px-0.5">
        {Check_(c.len, "12+ characters")}
        {Check_(c.upper, "1 capital letter")}
        {Check_(c.num, "1 number")}
        {Check_(c.sym, "1 symbol")}
      </div>
      {confirm.length > 0 && !match && <p className="text-xs" style={{ color: "#e06b70" }}>Passwords don't match.</p>}
      {error && <p className="text-xs" data-testid="new-password-error" style={{ color: "#e06b70" }}>{error}</p>}
      <button type="submit" disabled={!canSubmit} data-testid="new-password-submit"
        className="w-full rounded-xl py-3.5 font-semibold text-sm" style={{ background: "var(--accent)", color: "#fff", opacity: canSubmit ? 1 : 0.5 }}>
        {loading ? "Saving…" : submitLabel}
      </button>
    </form>
  );
}

// 8-digit reset code entry (emailed to the user).
export function ResetCodeForm({ email, onSubmit, onResend, error, loading }) {
  const [code, setCode] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(code.trim()); }} data-testid="reset-code-form" className="space-y-3">
      <div className="flex items-center gap-2 gold-text"><ShieldCheck size={18} /><span className="font-semibold text-sm">Enter your reset code</span></div>
      <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
        We sent an 8-digit code to <span style={{ color: "var(--text-primary)" }}>{email}</span>. It expires in 10 minutes.
      </p>
      <input data-testid="reset-code-input" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} inputMode="numeric" maxLength={8} placeholder="12345678" autoFocus
        className="w-full rounded-xl px-4 py-3.5 text-sm outline-none tracking-[0.4em] text-center"
        style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)", fontSize: 16 }} />
      {error && <p className="text-xs" data-testid="reset-code-error" style={{ color: "#e06b70" }}>{error}</p>}
      <button type="submit" disabled={loading} data-testid="reset-code-submit"
        className="w-full rounded-xl py-3.5 font-semibold text-sm" style={{ background: "var(--accent)", color: "#fff", opacity: loading ? 0.6 : 1 }}>
        {loading ? "Checking…" : "Continue"}
      </button>
      <button type="button" onClick={onResend} data-testid="reset-code-resend" className="w-full text-center text-xs" style={{ color: "var(--text-secondary)" }}>
        Didn't get it? Send a new code
      </button>
    </form>
  );
}